import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from "@angular/core";
import { ElementUtilities } from "@common/lib/utilities/element-utilities";
import { switchMap } from "rxjs";
import { IFocusable } from "../adapt-common-dialog/focusable";
import { AdaptHtmlEditorFroalaComponent } from "./html-editor-froala/html-editor-froala.component";

@Component({
    selector: "adapt-html-editor",
    template: `
        <adapt-html-editor-froala [contents]="contents"
                                  (contentHeightChanged)="onContentHeightChanged($event)"
                                  (contentsChange)="contentsChange.emit($event)"
                                  (isValidChange)="isValidChange.emit($event)"
                                  [forceSize]="forceSize"
                                  [placeholderText]="placeholderText"
                                  [required]="required"
                                  [onlyShowToolbarWhenFocussed]="onlyShowToolbarWhenFocussed"
                                  [readonly]="readonly"
                                  [showCodeView]="isValidChange.observed"
                                  [minHeight]="minHeight"
                                  [useMentions]="useMentions">
        </adapt-html-editor-froala>`,
    styleUrls: ["./html-editor.component.scss"],
    // needed to target editor elements
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class AdaptHtmlEditorComponent implements IFocusable, OnChanges {
    @Input() public contents?: string | null;
    @Output() public contentsChange = new EventEmitter<string>();
    @Output() public isValidChange = new EventEmitter<boolean>();

    @Input() public minHeight?: number;

    @Input() public forceSize: "standard" | "compact" = "standard";
    @Input() public placeholderText = "Type something";
    @Input() public required = false;
    @Input() public onlyShowToolbarWhenFocussed = false;
    @Input() public readonly = false;
    @Input() public useMentions = false;

    private heightOverride = false;
    private lastHeight?: number;

    @ViewChild(AdaptHtmlEditorFroalaComponent) private froalaHtmlEditor?: AdaptHtmlEditorFroalaComponent;

    public constructor(public elementRef: ElementRef) { }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.readonly) {
            const element = this.elementRef.nativeElement as HTMLElement;
            if (!this.readonly) {
                // readonly changed from true to false - remove previously overwritten max-height in style if there is any
                if (element.style.maxHeight === "none") {
                    element.style.maxHeight = "";
                }
            } else {
                // readonly changed from false to true - set max-height to none
                if (element.style.maxHeight !== "none") {
                    element.style.maxHeight = "none";
                }

                if (this.heightOverride && element.style.height) {
                    // remove height style if it has been set previously
                    element.style.height = "";
                    this.heightOverride = false;
                }
            }

            this.froalaHtmlEditor?.refreshContentScroller();
        }
    }

    public getElementToFocus() {
        return this.froalaHtmlEditor?.getElementToFocus();
    }

    public onContentHeightChanged(height: number) {
        if (this.readonly) {
            return;
        }

        this.lastHeight = height;
        // Handle content height changes
        const element = this.elementRef.nativeElement as HTMLElement;
        const computedMaxHeight = getComputedStyle(element).maxHeight;
        const currentContainerHeight = element.getBoundingClientRect().height;
        const calculatedEditorHeight = height + 34; // 34 is the height of toolbar
        if (calculatedEditorHeight > currentContainerHeight && !element.style.height && computedMaxHeight !== "none") {
            // usage of html-editor not intending to override height (e.g. only set max height) - need to set height for the scrollbar to work
            // - otherwise, don't override
            // computedMaxHeight is always in the form of "333px" even if that's set to calc(100vh - blah)
            // - parseInt will convert it to a number discarding the "px" suffix
            const maxHeightValue = parseInt(computedMaxHeight, 10);
            if (calculatedEditorHeight > maxHeightValue) {
                element.style.height = `${currentContainerHeight}px`;
                this.heightOverride = true;
            }
        } else if (calculatedEditorHeight <= currentContainerHeight && this.heightOverride && element.style.height) {
            // remove height style if it has been set previously
            element.style.height = "";
            this.heightOverride = false;
        }
    }

    public setElementMaxHeightToViewportBottom(additionalBottomOffset = 0) {
        const element = this.elementRef.nativeElement as HTMLElement;
        ElementUtilities.waitUntilVisible(element).pipe(
            switchMap(() => ElementUtilities.waitUntilPositionStable(element)),
        ).subscribe(() => {
            const top = element.getBoundingClientRect().top ?? 0;
            element.style.maxHeight = `calc(100vh - ${(top + 1)}px - 140px - ${additionalBottomOffset}px)`; // 140px is the min gap covering dialog footer and the gap after that
            if (this.lastHeight) {
                setTimeout(() => this.onContentHeightChanged(this.lastHeight!));
            }
        });
    }
}
